import React, { useState, useMemo, useEffect, useCallback } from 'react';
import round from 'lodash/round';
import { AssetType, DebitOrCredit, OrderAction, OrderType } from '@tradingblock/types';
import { useOrderData, OrderState } from '../state/OrderState';
import { useOrderState } from '../state/useOrderState';
import { useOrderActions } from '../state/useOrderActions';
import { useOrderStatus } from '../data/useOrderStatus';
import { useOrderPrice } from '../hooks/useOrderPrice';
import { MarkerLabel } from '../OrderContent';
import Slider from 'rc-slider';
import _ from 'lodash';
import { orderLegsToClassifiedStrategy } from '../../../data/global/utilities/strategyClassification';
import { OrderSelectors } from '../state/OrderSelector';
import { determineIsBuy } from '../OrderUtilities';

export const PriceSlider: React.FC = () => {
  const { bid, mid, ask } = useOrderPrice();
  const { setPrice, setStopPrice, setAction } = useOrderActions();
  const orderType = useOrderState('orderType');
  const orderAction = useOrderState('action');
  const isCustomOrder = useOrderData((s: OrderState) => s.strategy === 'Custom');
  const legs = useOrderData(OrderSelectors.legs);
  const isValid = useOrderData(s => s.validation && s.validation.valid);
  const isStopMarketOrder = orderType === OrderType.Stop_Market;
  const isStopLimitOrder = orderType === OrderType.Stop_Limit;
  const classifiedStrategy = orderLegsToClassifiedStrategy(legs);
  const isStrategyMatch = classifiedStrategy.name !== 'Custom';
  const debitCredit = useOrderData(s => s.debitCredit);
  const price = useOrderData((s: OrderState) => s.price, undefined);
  const stopPrice = useOrderData((s: OrderState) => s.stopPrice, undefined);
  const [lowDot, setLowDot] = useState<number>(25);
  const [highDot, setHighDot] = useState<number>(75);
  const [min, setMin] = useState<number>(0);
  const [max, setMax] = useState<number>(100);

  const isCustomWithoutClassifiedStrategy = isCustomOrder && !isStrategyMatch;
  const isClassifiedAsCalendar = _.includes(classifiedStrategy.name, 'Calendar');

  const isBuy = determineIsBuy(
    isCustomOrder,
    orderAction,
    isClassifiedAsCalendar,
    isStrategyMatch,
    classifiedStrategy,
    isCustomWithoutClassifiedStrategy,
    debitCredit,
    legs,
    price
  );

  useEffect(() => {
    if (bid + mid + ask !== 0) {
      const lowDistance = parseInt((Math.abs(mid - bid) * 100).toFixed(0));
      const highDistance = parseInt((Math.abs(ask - mid) * 100).toFixed(0));
      const maxDistance = lowDistance > highDistance ? lowDistance : highDistance;
      const maxSpread = maxDistance * 2;
      setMin(50 - maxSpread);
      setMax(50 + maxSpread);
      setLowDot(50 - maxDistance);
      setHighDot(50 + maxDistance);
    }
  }, [bid, mid, ask]);

  const { complete } = useOrderStatus();
  const isMarketOrder = orderType === OrderType.Market;
  const num_legs = legs ? Object.values(legs).length : 1;

  const setPriceFromSlider = (value: number | number[]) => {
    if (typeof value === 'number' && complete !== false && orderType !== OrderType.Market) {
      // Account for reversed slider state
      const adjustedValue = isBuy ? value : 100 - value;

      let bottom = bid > 0 ? bid - bid / 25 : bid + bid / 25;
      let top = ask > 0 ? ask + ask / 25 : ask - ask / 25;
      let updatedPrice = parseFloat(((adjustedValue / 100) * (top - bottom) + bottom).toFixed(2));

      updatedPrice = (adjustedValue - 50) / 100 + mid;

      let priceParts = updatedPrice.toString().split('.');

      if (priceParts.length > 1 && priceParts[1].length > 2) {
        updatedPrice = parseFloat(updatedPrice.toFixed(2));
      }
      if (isStopMarketOrder) {
        setPrice(updatedPrice, true, updatedPrice < 0);
        setStopPrice(updatedPrice);
      } else if (isStopLimitOrder) {
        if (stopPrice === undefined) setStopPrice(updatedPrice);
        else setPrice(updatedPrice, true, updatedPrice < 0);
      } else {
        setPrice(updatedPrice, true, updatedPrice < 0);
      }
    }
  };

  const priceSliderValue = useMemo(() => {
    if (price === undefined || isMarketOrder) {
      return min;
    }
    return (isBuy ? 1 : -1) * parseFloat((price - mid).toFixed(2)) * 100 + 50;
  }, [bid, mid, ask, price, isBuy, min]);

  return (
    <Slider
      step={1}
      className={isBuy ? '' : 'slider-is-reversed'}
      min={min}
      max={max}
      value={priceSliderValue}
      onChange={setPriceFromSlider}
      disabled={isMarketOrder || !isValid || !complete}
      reverse={!isBuy}
      handleStyle={{
        top: '-4px',
        userSelect: 'none', // Prevents text selection
        WebkitUserSelect: 'none', // Safari support
        MozUserSelect: 'none', // Firefox support
        msUserSelect: 'none', // IE support
        padding: '10px',
      }}
      marks={
        isBuy
          ? {
              [lowDot]: {
                label: (
                  <MarkerLabel
                    styles={{
                      top: num_legs > 1 ? '-3.2rem' : '-2.2rem',
                    }}
                    title={(num_legs > 1 ? 'Impl. ' : '') + 'Bid'}
                    value={
                      complete === false ? '-' : bid.toLocaleString(undefined, { currency: 'USD', style: 'currency' })
                    }
                  />
                ),
                style: {
                  userSelect: 'none',
                },
              },
              [50]: {
                label: (
                  <MarkerLabel
                    styles={{
                      top: num_legs > 1 ? '-3.2rem' : '-2.2rem',
                    }}
                    title={(num_legs > 1 ? 'Impl. ' : '') + 'Mid'}
                    value={
                      complete === false ? '-' : mid.toLocaleString(undefined, { currency: 'USD', style: 'currency' })
                    }
                  />
                ),
                style: {
                  userSelect: 'none',
                },
              },
              [highDot]: {
                label: (
                  <MarkerLabel
                    styles={{
                      top: num_legs > 1 ? '-3.2rem' : '-2.2rem',
                    }}
                    title={(num_legs > 1 ? 'Impl. ' : '') + 'Ask'}
                    value={
                      complete === false ? '-' : ask.toLocaleString(undefined, { currency: 'USD', style: 'currency' })
                    }
                  />
                ),
                style: {
                  userSelect: 'none',
                },
              },
            }
          : {
              [lowDot]: {
                label: (
                  <MarkerLabel
                    styles={{
                      top: num_legs > 1 ? '-3.2rem' : '-2.2rem',
                    }}
                    title={(num_legs > 1 ? 'Impl. ' : '') + 'Ask'}
                    value={
                      complete === false ? '-' : ask.toLocaleString(undefined, { currency: 'USD', style: 'currency' })
                    }
                    isReversed={true}
                  />
                ),
                style: {
                  userSelect: 'none',
                },
              },
              [50]: {
                label: (
                  <MarkerLabel
                    styles={{
                      top: num_legs > 1 ? '-3.2rem' : '-2.2rem',
                    }}
                    title={(num_legs > 1 ? 'Impl. ' : '') + 'Mid'}
                    value={
                      complete === false ? '-' : mid.toLocaleString(undefined, { currency: 'USD', style: 'currency' })
                    }
                    isReversed={true}
                  />
                ),
                style: {
                  userSelect: 'none',
                },
              },
              [highDot]: {
                label: (
                  <MarkerLabel
                    styles={{
                      top: num_legs > 1 ? '-3.2rem' : '-2.2rem',
                    }}
                    title={(num_legs > 1 ? 'Impl. ' : '') + 'Bid'}
                    value={
                      complete === false ? '-' : bid.toLocaleString(undefined, { currency: 'USD', style: 'currency' })
                    }
                    isReversed={true}
                  />
                ),
                style: {
                  userSelect: 'none',
                },
              },
            }
      }
    />
  );
};
